import type { QueueTreeNode, QueueTreeResponse } from "../../experience-shell/queues/types";

/**
 * Lookup function returning the human-readable name for a queue id, or
 * `undefined` when the id isn't known to the current queue tree (e.g.
 * the queue has been deleted since the work item was written, or the
 * tree hasn't loaded yet). Callers should treat the absence as
 * "render no chip" rather than "render an empty chip" — empty chips
 * imply the routing failed, which is worse than no signal at all.
 */
export type QueueNameResolver = (queueId: string) => string | undefined;

/**
 * Collapse the queue tree into a flat `Map<id, name>`. Callers walk
 * the tree exactly once (typically inside `useMemo` keyed on the tree
 * response) and then close over the resulting map via a
 * `QueueNameResolver` callback. Doing the flattening up here means
 * the card components stay ignorant of the tree's shape and can be
 * unit-tested with a stub resolver.
 */
export function flattenQueueTree(roots: readonly QueueTreeNode[]): Map<string, string> {
    const out = new Map<string, string>();
    const visit = (node: QueueTreeNode): void => {
        out.set(node.queue.id, node.queue.name);
        for (const child of node.children) visit(child);
    };
    for (const root of roots) visit(root);
    return out;
}

/**
 * Build a `QueueNameResolver` straight from a tree response — the
 * common path. The memo boundary is the caller's responsibility (the
 * map allocation is cheap but the closure identity matters for React's
 * referential-equality bailouts).
 */
export function buildQueueNameResolver(tree: QueueTreeResponse | undefined): QueueNameResolver {
    const map = tree !== undefined ? flattenQueueTree(tree.roots) : new Map<string, string>();
    return (queueId) => map.get(queueId);
}

/** Separator between ancestor names, root first. */
export const ANCESTOR_PATH_SEPARATOR = " \u203A ";

/**
 * Render a queue's `ancestorPath` (root-first queue ids on the wire) as a
 * readable breadcrumb of names. Returns `null` when the path is empty or
 * any id is unknown to the tree: a caption that mixes names with raw ids
 * misleads more than no caption.
 */
export function formatAncestorPath(
    ancestorPath: readonly string[],
    resolveQueueName: QueueNameResolver,
): string | null {
    if (ancestorPath.length === 0) return null;
    const names: string[] = [];
    for (const id of ancestorPath) {
        const name = resolveQueueName(id);
        if (name === undefined) return null;
        names.push(name);
    }
    return names.join(ANCESTOR_PATH_SEPARATOR);
}

export interface RecipientSummary {
    readonly label: string;
    readonly count: number;
    readonly names: readonly string[];
}

/**
 * Resolve a work item's queue ids into a compact display label such as
 * "Vincent Vogelaar · Customer Success" (≤2 names) or
 * "Vincent Vogelaar · +3 more" (3+). Returns `null` when no ids
 * resolve — that signals the caller to skip the chip entirely rather
 * than render an empty pill.
 *
 * The full `names` list is returned alongside so callers can show
 * tooltips that reveal every recipient even when the label truncates.
 */
export function summarizeRecipients(
    queueIds: readonly string[],
    resolveQueueName: QueueNameResolver,
): RecipientSummary | null {
    const names: string[] = [];
    for (const id of queueIds) {
        const name = resolveQueueName(id);
        if (name !== undefined) names.push(name);
    }
    if (names.length === 0) return null;
    const [first, second] = names;
    let label: string;
    if (names.length === 1) {
        label = first ?? "";
    } else if (names.length === 2) {
        label = `${first ?? ""} · ${second ?? ""}`;
    } else {
        label = `${first ?? ""} · +${String(names.length - 1)} more`;
    }
    return { label, count: names.length, names };
}

// ---------------------------------------------------------------------------
// Routing (sender → recipients)
//
// `summarizeRecipients` collapses a single queue list into one label —
// useful for surfaces that only need to answer "who can see this?" (note
// cards, task cards). Message bubbles want a richer answer: "who SENT
// this, and who is it going TO?", because the conversational direction
// is itself information the asker / responder need to read at a glance.
//
// The split is honest about a domain distinction the work item already
// carries — `requestedByQueueId` is the originating queue, captured at
// write time from the @-mention composer; `visibleToQueueIds` is the
// audience union (sender + recipients). Filtering the sender out of the
// recipient list keeps the chip from saying "Jason Gray's Queue · Jason
// Gray's Queue → Enterprise."
// ---------------------------------------------------------------------------

export interface RoutingSender {
    readonly id: string;
    readonly name: string;
}

export interface RoutingSummary {
    readonly sender: RoutingSender | null;
    readonly recipients: RecipientSummary | null;
}

export interface RoutingInput {
    /**
     * Originating queue for an @-mention-spawned item. Null for normal
     * (non-spawned) items — those have no semantic sender and render as
     * the recipient summary alone.
     */
    readonly requestedByQueueId: string | null;
    /**
     * Display-name snapshot captured at write time. Preferred over a
     * live resolver lookup so renames after the fact don't rewrite
     * history, and so a since-deleted sender queue still renders with
     * its original label.
     */
    readonly requestedByQueueName: string | null;
    /**
     * Full audience (sender + recipients). The sender id is filtered
     * out before recipient summarization so the same queue never
     * appears on both sides of the arrow.
     */
    readonly visibleToQueueIds: readonly string[];
}

/**
 * Resolve a work item's routing into separate sender + recipients
 * structures suitable for a "sender → recipients" chip layout.
 *
 *  - When `requestedByQueueId` is null, `sender` is null and
 *    `recipients` falls back to summarizing the full visibility list
 *    — the legacy single-pill behavior for items that predate the
 *    @-mention flow.
 *  - When `requestedByQueueId` is set but only the sender appears in
 *    `visibleToQueueIds` (a self-spawned or single-queue item),
 *    `recipients` is null and the caller renders the sender chip
 *    alone — honest about "this is attributed to that queue, no other
 *    queues see it."
 *  - When the sender's display name can't be resolved (no snapshot,
 *    and the live resolver has nothing for the id either), the sender
 *    is treated as unknown and dropped to null. An unnamed chip is
 *    worse than no chip.
 */
export function summarizeRouting(
    input: RoutingInput,
    resolveQueueName: QueueNameResolver,
): RoutingSummary {
    const senderId = input.requestedByQueueId;
    const senderName =
        input.requestedByQueueName ??
        (senderId !== null ? resolveQueueName(senderId) ?? null : null);

    const sender: RoutingSender | null =
        senderId !== null && senderName !== null
            ? { id: senderId, name: senderName }
            : null;

    const recipientIds =
        sender !== null
            ? input.visibleToQueueIds.filter((id) => id !== sender.id)
            : input.visibleToQueueIds;
    const recipients = summarizeRecipients(recipientIds, resolveQueueName);

    return { sender, recipients };
}
